import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import socketService from '../services/socketService';

// Types
interface RealtimeState {
  isConnected: boolean;
  socketId: string | null;
  activeCalls: any[];
  waitingCalls: any[];
  activeAgents: any[];
  realTimeStats: any;
  notifications: any[];
  alerts: any[];
  systemMessages: any[];
  systemHealth: any;
  loading: boolean;
  error: string | null;
}

interface RealtimeAction {
  type: string;
  payload?: any;
}

interface RealtimeContextType {
  isConnected: boolean;
  socketId: string | null;
  activeCalls: any[];
  waitingCalls: any[];
  activeAgents: any[];
  realTimeStats: any;
  notifications: any[];
  alerts: any[];
  systemMessages: any[];
  systemHealth: any;
  loading: boolean;
  error: string | null;
  joinUserRoom: (userId: string) => void;
  joinSupervisorRoom: () => void;
  joinQueueRoom: (queueId: string) => void;
  leaveQueueRoom: (queueId: string) => void;
  requestRealTimeStats: () => void;
  requestAgentPerformance: (agentId: string) => void;
  getActiveCalls: () => void;
  getWaitingCalls: () => void;
  getSystemHealth: () => void;
  sendNotification: (userId: string, message: string, type?: string) => void;
  sendAlert: (userId: string, message: string, severity?: string) => void;
  sendSystemMessage: (message: string, target?: string) => void;
  broadcastSupervisorMessage: (message: string, type?: string) => void;
  clearNotifications: () => void;
  clearAlerts: () => void;
  clearSystemMessages: () => void;
  clearError: () => void;
}

// Realtime Context
const RealtimeContext = createContext<RealtimeContextType | undefined>(undefined);

// Realtime Reducer
const realtimeReducer = (state: RealtimeState, action: RealtimeAction): RealtimeState => {
  switch (action.type) {
    case 'SET_CONNECTED':
      return { ...state, isConnected: action.payload, loading: false };
    case 'SET_SOCKET_ID':
      return { ...state, socketId: action.payload };
    case 'SET_ACTIVE_CALLS':
      return { ...state, activeCalls: action.payload, loading: false };
    case 'SET_WAITING_CALLS':
      return { ...state, waitingCalls: action.payload, loading: false };
    case 'SET_ACTIVE_AGENTS':
      return { ...state, activeAgents: action.payload, loading: false };
    case 'SET_REAL_TIME_STATS':
      return { ...state, realTimeStats: action.payload, loading: false };
    case 'ADD_NOTIFICATION':
      return { 
        ...state, 
        notifications: [...state.notifications, { ...action.payload, id: Date.now(), timestamp: new Date().toISOString() }]
      };
    case 'ADD_ALERT':
      return { 
        ...state, 
        alerts: [...state.alerts, { ...action.payload, id: Date.now(), timestamp: new Date().toISOString() }]
      };
    case 'ADD_SYSTEM_MESSAGE':
      return { 
        ...state, 
        systemMessages: [...state.systemMessages, { ...action.payload, id: Date.now(), timestamp: new Date().toISOString() }]
      };
    case 'SET_SYSTEM_HEALTH':
      return { ...state, systemHealth: action.payload, loading: false };
    case 'CLEAR_NOTIFICATIONS':
      return { ...state, notifications: [] };
    case 'CLEAR_ALERTS':
      return { ...state, alerts: [] };
    case 'CLEAR_SYSTEM_MESSAGES':
      return { ...state, systemMessages: [] };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
};

// Initial State
const initialState: RealtimeState = {
  isConnected: false,
  socketId: null,
  activeCalls: [],
  waitingCalls: [],
  activeAgents: [],
  realTimeStats: null,
  notifications: [],
  alerts: [],
  systemMessages: [],
  systemHealth: null,
  loading: false,
  error: null
};

// Realtime Provider
interface RealtimeProviderProps {
  children: ReactNode;
}

export const RealtimeProvider = ({ children }: RealtimeProviderProps) => {
  const [state, dispatch] = useReducer(realtimeReducer, initialState);

  // Setup socket event listeners
  useEffect(() => {
    // Connection events
    socketService.on('socket-connected', (data: any) => {
      dispatch({ type: 'SET_CONNECTED', payload: true });
      dispatch({ type: 'SET_SOCKET_ID', payload: data.socketId });
    });

    socketService.on('socket-disconnected', () => {
      dispatch({ type: 'SET_CONNECTED', payload: false });
      dispatch({ type: 'SET_SOCKET_ID', payload: null });
    });

    socketService.on('socket-error', (data: any) => {
      dispatch({ type: 'SET_ERROR', payload: data.error });
    });

    // Call events
    socketService.on('active-calls', (data: any) => {
      dispatch({ type: 'SET_ACTIVE_CALLS', payload: data });
    });

    socketService.on('waiting-calls', (data: any) => {
      dispatch({ type: 'SET_WAITING_CALLS', payload: data });
    });

    socketService.on('call-status-update', (data: any) => {
      // Update active calls when status changes
      dispatch({ type: 'SET_LOADING', payload: true });
      // Trigger refresh of active calls
      setTimeout(() => {
        socketService.getActiveCalls();
      }, 100);
    });

    // Real-time stats
    socketService.on('real-time-stats', (data: any) => {
      dispatch({ type: 'SET_REAL_TIME_STATS', payload: data });
    });

    socketService.on('agent-performance-update', (data: any) => {
      // Update agent performance in real-time stats
      const currentStats = state.realTimeStats;
      if (currentStats) {
        const updatedStats = {
          ...currentStats,
          agentPerformance: data,
          timestamp: new Date().toISOString()
        };
        dispatch({ type: 'SET_REAL_TIME_STATS', payload: updatedStats });
      }
    });

    // Notification events
    socketService.on('notification', (data: any) => {
      dispatch({ type: 'ADD_NOTIFICATION', payload: data });
    });

    socketService.on('alert', (data: any) => {
      dispatch({ type: 'ADD_ALERT', payload: data });
    });

    socketService.on('system-message', (data: any) => {
      dispatch({ type: 'ADD_SYSTEM_MESSAGE', payload: data });
    });

    // System health
    socketService.on('system-health', (data: any) => {
      dispatch({ type: 'SET_SYSTEM_HEALTH', payload: data });
    });

    // Agent events
    socketService.on('agent-login', (data: any) => {
      // Refresh active agents
      setTimeout(() => {
        socketService.getActiveCalls();
      }, 100);
    });

    socketService.on('agent-logout', (data: any) => {
      // Refresh active agents
      setTimeout(() => {
        socketService.getActiveCalls();
      }, 100);
    });

    socketService.on('agent-status-change', (data: any) => {
      // Refresh active agents
      setTimeout(() => {
        socketService.getActiveCalls();
      }, 100);
    });

    // Queue events
    socketService.on('queue-status-update', (data: any) => {
      // Refresh waiting calls
      setTimeout(() => {
        socketService.getWaitingCalls();
      }, 100);
    });

    socketService.on('queue-call-assigned', (data: any) => {
      // Refresh active and waiting calls
      setTimeout(() => {
        socketService.getActiveCalls();
        socketService.getWaitingCalls();
      }, 100);
    });

    // Recording events
    socketService.on('recording-started', (data: any) => {
      dispatch({ type: 'ADD_NOTIFICATION', payload: {
        type: 'info',
        message: `Kayıt başlatıldı: ${data.callId}`,
        data
      }});
    });

    socketService.on('recording-stopped', (data: any) => {
      dispatch({ type: 'ADD_NOTIFICATION', payload: {
        type: 'info',
        message: `Kayıt durduruldu: ${data.callId}`,
        data
      }});
    });

    socketService.on('recording-error', (data: any) => {
      dispatch({ type: 'ADD_ALERT', payload: {
        severity: 'error',
        message: `Kayıt hatası: ${data.error}`,
        data
      }});
    });

    // Customer events
    socketService.on('customer-data-updated', (data: any) => {
      dispatch({ type: 'ADD_NOTIFICATION', payload: {
        type: 'success',
        message: `Müşteri bilgileri güncellendi: ${data.customerId}`,
        data
      }});
    });

    socketService.on('customer-note-added', (data: any) => {
      dispatch({ type: 'ADD_NOTIFICATION', payload: {
        type: 'info',
        message: `Yeni not eklendi: ${data.customerId}`,
        data
      }});
    });

    // Error events
    socketService.on('call-error', (data: any) => {
      dispatch({ type: 'ADD_ALERT', payload: {
        severity: 'error',
        message: `Çağrı hatası: ${data.error}`,
        data
      }});
    });

    socketService.on('system-error', (data: any) => {
      dispatch({ type: 'ADD_ALERT', payload: {
        severity: 'error',
        message: `Sistem hatası: ${data.error}`,
        data
      }});
    });

    // Cleanup function
    return () => {
      // Remove all event listeners
      socketService.off('socket-connected', () => {});
      socketService.off('socket-disconnected', () => {});
      socketService.off('socket-error', () => {});
      socketService.off('active-calls', () => {});
      socketService.off('waiting-calls', () => {});
      socketService.off('call-status-update', () => {});
      socketService.off('real-time-stats', () => {});
      socketService.off('agent-performance-update', () => {});
      socketService.off('notification', () => {});
      socketService.off('alert', () => {});
      socketService.off('system-message', () => {});
      socketService.off('system-health', () => {});
      socketService.off('agent-login', () => {});
      socketService.off('agent-logout', () => {});
      socketService.off('agent-status-change', () => {});
      socketService.off('queue-status-update', () => {});
      socketService.off('queue-call-assigned', () => {});
      socketService.off('recording-started', () => {});
      socketService.off('recording-stopped', () => {});
      socketService.off('recording-error', () => {});
      socketService.off('customer-data-updated', () => {});
      socketService.off('customer-note-added', () => {});
      socketService.off('call-error', () => {});
      socketService.off('system-error', () => {});
    };
  }, [state.realTimeStats]);

  // Context methods
  const joinUserRoom = (userId: string) => {
    socketService.joinUserRoom(userId);
  };

  const joinSupervisorRoom = () => {
    socketService.joinSupervisorRoom();
  };

  const joinQueueRoom = (queueId: string) => {
    socketService.joinQueueRoom(queueId);
  };

  const leaveQueueRoom = (queueId: string) => {
    socketService.leaveQueueRoom(queueId);
  };

  const requestRealTimeStats = () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    socketService.requestRealTimeStats();
  };

  const requestAgentPerformance = (agentId: string) => {
    socketService.requestAgentPerformance(agentId);
  };

  const getActiveCalls = () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    socketService.getActiveCalls();
  };

  const getWaitingCalls = () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    socketService.getWaitingCalls();
  };

  const getSystemHealth = () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    socketService.getSystemHealth();
  };

  const sendNotification = (userId: string, message: string, type: string = 'info') => {
    socketService.sendNotification(userId, message, type);
  };

  const sendAlert = (userId: string, message: string, severity: string = 'warning') => {
    socketService.sendAlert(userId, message, severity);
  };

  const sendSystemMessage = (message: string, target: string = 'all') => {
    socketService.sendSystemMessage(message, target);
  };

  const broadcastSupervisorMessage = (message: string, type: string = 'info') => {
    socketService.broadcastSupervisorMessage(message, type);
  };

  const clearNotifications = () => {
    dispatch({ type: 'CLEAR_NOTIFICATIONS' });
  };

  const clearAlerts = () => {
    dispatch({ type: 'CLEAR_ALERTS' });
  };

  const clearSystemMessages = () => {
    dispatch({ type: 'CLEAR_SYSTEM_MESSAGES' });
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Context value
  const value: RealtimeContextType = {
    ...state,
    joinUserRoom,
    joinSupervisorRoom,
    joinQueueRoom,
    leaveQueueRoom,
    requestRealTimeStats,
    requestAgentPerformance,
    getActiveCalls,
    getWaitingCalls,
    getSystemHealth,
    sendNotification,
    sendAlert,
    sendSystemMessage,
    broadcastSupervisorMessage,
    clearNotifications,
    clearAlerts,
    clearSystemMessages,
    clearError
  };

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
};

// Custom hook to use realtime context
export const useRealtime = (): RealtimeContextType => {
  const context = useContext(RealtimeContext);
  if (context === undefined) {
    throw new Error('useRealtime must be used within a RealtimeProvider');
  }
  return context;
};

export default RealtimeContext;
